import { useEffect, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ArrowUp, Share2 } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";
import { PHONE_TEL, WHATSAPP_NUMBER, INSTAGRAM_URL, FACEBOOK_URL, EMAIL } from "../content/siteContent";
import { IconPhone, IconWhatsApp, IconInstagram, IconFacebook, IconMail, IconClose } from "./Icons";

const MobileCtaBar = () => {
    const { t } = useLanguage();
    const reduce = useReducedMotion();
    const [visible, setVisible] = useState(false);
    const [socialOpen, setSocialOpen] = useState(false);
    const [tick, setTick] = useState(0);

    useEffect(() => {
        const onScroll = () => setVisible(window.scrollY > 480);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        if (!socialOpen) return undefined;
        const timer = setTimeout(() => setSocialOpen(false), 5000);
        return () => clearTimeout(timer);
    }, [socialOpen, tick]);

    const socialLinks = [
        { id: "whatsapp", href: `https://wa.me/${WHATSAPP_NUMBER}`, label: t.contact.whatsappLabel, Icon: IconWhatsApp, external: true },
        { id: "instagram", href: INSTAGRAM_URL, label: t.contact.instagramLabel, Icon: IconInstagram, external: true },
        { id: "facebook", href: FACEBOOK_URL, label: t.contact.facebookLabel, Icon: IconFacebook, external: true },
        { id: "email", href: `mailto:${EMAIL}`, label: t.contact.emailLabel, Icon: IconMail, external: false },
    ];

    return (
        <div
            className={`fixed inset-x-0 bottom-0 z-40 grid grid-cols-[1fr_1fr_auto_auto] gap-2 border-t border-ink/10 bg-sand/90 p-2 backdrop-blur-xl transition-transform duration-300 md:hidden ${
                visible ? "translate-y-0" : "pointer-events-none translate-y-full"
            }`}
            style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
            data-testid="mobile-cta-bar"
            aria-hidden={!visible}
        >
            <AnimatePresence>
                {socialOpen && (
                    <motion.div
                        className="absolute bottom-full end-2 mb-2 flex flex-col items-center gap-2.5"
                        initial={reduce ? false : { opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onPointerDown={() => setTick((v) => v + 1)}
                    >
                        {socialLinks.map(({ id, href, label, Icon, external }, i) => {
                            const link = (
                                <a
                                    href={href}
                                    target={external ? "_blank" : undefined}
                                    rel={external ? "noopener noreferrer" : undefined}
                                    aria-label={label}
                                    data-testid={`mobile-social-${id}`}
                                    onClick={() => setSocialOpen(false)}
                                    className="flex h-11 w-11 items-center justify-center rounded-full border border-ink/10 bg-surface/90 text-pine shadow-[0_10px_25px_-12px_rgba(0,77,77,0.4)] backdrop-blur-xl transition-[background-color,color,transform,border-color] duration-300 hover:border-gold-500 hover:bg-pine hover:text-cream active:scale-90"
                                >
                                    <Icon className="h-5 w-5" />
                                </a>
                            );
                            if (reduce) return <div key={id}>{link}</div>;
                            return (
                                <motion.div
                                    key={id}
                                    style={{ transformOrigin: "bottom center" }}
                                    initial={{ opacity: 0, scale: 0.3, y: 18 }}
                                    animate={{ opacity: 1, scale: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.3, y: 18 }}
                                    transition={{ type: "spring", stiffness: 500, damping: 28, delay: socialOpen ? i * 0.06 : 0 }}
                                >
                                    {link}
                                </motion.div>
                            );
                        })}
                    </motion.div>
                )}
            </AnimatePresence>
            <a
                href={`tel:${PHONE_TEL}`}
                data-testid="mobile-bar-call"
                className="flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-pine text-sm font-semibold text-cream transition-colors duration-300 active:bg-pine-600"
            >
                <IconPhone className="h-4 w-4" />
                {t.mobileBar.call}
            </a>
            <a
                href="#appointment"
                data-testid="mobile-bar-book"
                className="flex min-h-[48px] items-center justify-center gap-2 rounded-full bg-gold-500 text-sm font-semibold text-ink transition-colors duration-300 active:bg-gold-600"
            >
                <IconWhatsApp className="h-4 w-4" />
                {t.mobileBar.book}
            </a>
            <button
                type="button"
                aria-expanded={socialOpen}
                aria-label={t.footer.contactTitle}
                data-testid="mobile-bar-social"
                onClick={() => setSocialOpen((v) => !v)}
                className="flex h-12 w-12 items-center justify-center self-center rounded-full border border-ink/10 bg-surface text-pine transition-[background-color,transform] duration-300 active:scale-90"
            >
                <motion.span
                    animate={reduce ? {} : { rotate: socialOpen ? 90 : 0 }}
                    transition={{ duration: 0.25 }}
                    className="flex"
                >
                    {socialOpen ? (
                        <IconClose className="h-5 w-5" />
                    ) : (
                        <Share2 className="h-5 w-5" strokeWidth={1.75} aria-hidden="true" />
                    )}
                </motion.span>
            </button>
            <button
                type="button"
                onClick={() => window.scrollTo({ top: 0, left: 0 })}
                aria-label={t.backToTop}
                data-testid="mobile-bar-top"
                className="flex h-12 w-12 items-center justify-center self-center rounded-full border border-ink/10 bg-surface text-pine transition-[background-color,transform] duration-300 active:scale-90"
            >
                <ArrowUp className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
            </button>
        </div>
    );
};

export default MobileCtaBar;
